import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, ArrowRight, Sparkles } from "lucide-react";

const SESSION_KEY = "tm_centre_bot_session";

const OPENER = "Hi! I'm the Teacher4Me virtual assistant. I can share our NW Calgary hours, subjects, address, and help you book a free assessment. What can I help with?";

const SUGGESTIONS = [
  "What are your hours?",
  "How much is tutoring?",
  "Do you tutor Grade 8 math?",
  "Book a free assessment",
];

export default function CentreConciergeBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "opener", role: "assistant", text: OPENER },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const scrollRef = useRef(null);
  const nextId = useRef(1);
  const newMsgId = () => `m${nextId.current++}`;

  const sessionId = useRef(
    sessionStorage.getItem(SESSION_KEY) ||
    (() => {
      const id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
      return id;
    })()
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { id: newMsgId(), role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/api/centre/bot/chat", {
        session_id: sessionId.current, message: q,
      });
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", text: data.reply }]);
      if (data.lead && data.lead.interested) setLeadCaptured(true);
    } catch (err) {
      const msg =
        err?.response?.status === 429
          ? "You're going a bit fast — please try again in a minute."
          : err?.response?.data?.detail || "Sorry, I had trouble responding. Please try again or WhatsApp 825-365-8805.";
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", text: msg, error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="concierge-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 bg-[#D92D20] hover:bg-[#B91C1C] text-white
                     px-4 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold text-sm">
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline">Ask us anything</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] z-50
                     bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl flex flex-col
                     max-h-[75vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#064E3B] text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FDE68A]" />
              <div>
                <div className="font-display font-bold text-sm">Teacher4Me Assistant</div>
                <div className="text-[10px] opacity-80">NW Calgary · usually replies within seconds</div>
              </div>
            </div>
            <button data-testid="concierge-close" onClick={() => setOpen(false)}
                    className="p-1 rounded hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef}
               className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F9FAFB]"
               data-testid="concierge-messages">
            {messages.map((m, i) => (
              <div key={m.id}
                   className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                   data-testid={`msg-${m.role}-${i}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#D92D20] text-white rounded-br-sm"
                      : m.error
                        ? "bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] rounded-bl-sm"
                        : "bg-white text-[#111827] border border-[#E5E7EB] rounded-bl-sm"
                  }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-[#4B5563]">
                  <span className="inline-block animate-pulse">Thinking…</span>
                </div>
              </div>
            )}

            {/* Suggestions on first turn only */}
            {messages.length === 1 && !busy && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="concierge-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)}
                          data-testid={`concierge-suggest-${s.slice(0,10).replace(/\s+/g,'-').toLowerCase()}`}
                          className="text-xs bg-white border border-[#E5E7EB] hover:border-[#D92D20]
                                     px-3 py-1.5 rounded-full text-[#111827] font-semibold">
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* CTA banner after lead capture */}
            {leadCaptured && (
              <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3 text-sm text-[#065F46]"
                   data-testid="concierge-lead-captured">
                <div className="font-semibold">Thanks — a Teacher4Me team member will call you shortly.</div>
                <Link to="/free-assessment"
                      className="inline-flex items-center gap-1 mt-1 text-[#D92D20] font-semibold text-xs">
                  Or book online → <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#E5E7EB] p-3 flex items-center gap-2">
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message…"
              className="flex-1 border border-[#E5E7EB] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#D92D20]"
              maxLength={1000}
              disabled={busy}
            />
            <button type="submit" data-testid="concierge-send" disabled={busy || !input.trim()}
                    className="w-9 h-9 rounded-full bg-[#D92D20] hover:bg-[#B91C1C] disabled:opacity-40
                               text-white flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
          </form>

          <div className="text-[10px] text-[#4B5563] text-center pb-2">
            AI assistant · for anything urgent call{" "}
            <a href="tel:8253658805" className="font-semibold">825-365-8805</a>
          </div>
        </div>
      )}
    </>
  );
}
